import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TabsContent } from "@/components/ui/tabs";

interface DealInvestmentTabProps {
  kpis: any;
  onKpisChange: (data: any) => void;
  currency: string;
}

export function DealInvestmentTab({
  kpis,
  onKpisChange,
  currency,
}: DealInvestmentTabProps) {
  const handleBudgetChange = (field: string, value: number) => {
    const updatedKpis = {
      ...kpis,
      budget: {
        ...(kpis.budget || {}),
        [field]: value,
      },
    };
    onKpisChange(updatedKpis);
  };



  const handleCombinedChange = (
    budgetField: string,
    investmentField: string,
    value: number
  ) => {
    const updatedKpis = {
      ...kpis,
      budget: {
        ...(kpis.budget || {}),
        [budgetField]: value,
      },
      investmentBudget: {
        ...(kpis.investmentBudget || {}),
        [investmentField]: value,
      },
    };
    onKpisChange(updatedKpis);
  };

  const handlePurchasePriceChange = (value: number) => {
    const updatedKpis = {
      ...kpis,
      purchasePrice: value,
      purchase: {
        ...(kpis.purchase || {}),
        price: value,
      },
      budget: {
        ...(kpis.budget || {}),
        siteAcquisition: value,
      },
      investmentBudget: {
        ...(kpis.investmentBudget || {}),
        siteAcquisition: value,
        netPurchasePrice: value,
      },
    };
    onKpisChange(updatedKpis);
  };

  const calculateSiteAcquisition = () => {
    const purchasePrice =
      kpis.purchasePrice || kpis.budget?.siteAcquisition || 0;
    const transferTax = kpis.budget?.transferTax || 0;
    const dealCosts = kpis.budget?.dealCosts || 0;
    return purchasePrice + transferTax + dealCosts;
  };

  const calculateConstruction = () => {
    const construction =
      kpis.budget?.construction ||
      kpis.investmentBudget?.constructionCosts ||
      0;
    const ffoeOse = kpis.budget?.ffoeOse || kpis.investmentBudget?.ffeOse || 0;
    return construction + ffoeOse;
  };

  const calculateDevelopment = () => {
    const profFees =
      kpis.budget?.profFees || kpis.investmentBudget?.professionalFees || 0;
    const planningFees = kpis.budget?.planningFees || 0;
    const devFee =
      kpis.budget?.devFee || kpis.investmentBudget?.developmentFee || 0;
    const insuranceAdmin = kpis.budget?.insuranceAdmin || 0;
    const other = kpis.budget?.other || kpis.investmentBudget?.otherDev || 0;
    return profFees + planningFees + devFee + insuranceAdmin + other;
  };

  const calculatePreOpening = () => {
    return kpis.budget?.preopening || kpis.investmentBudget?.preOpening || 0;
  };

  const calculateContingency = () => {
    const subtotal =
      calculateSiteAcquisition() +
      calculateConstruction() +
      calculateDevelopment() +
      calculatePreOpening();
    const contingencyPct =
      kpis.budget?.contingencyPct ||
      kpis.investmentBudget?.contingencyPct ||
      10;
    return subtotal * (contingencyPct / 100);
  };

  const calculateGrandTotal = () => {
    const subtotal =
      calculateSiteAcquisition() +
      calculateConstruction() +
      calculateDevelopment() +
      calculatePreOpening();
    const contingency = calculateContingency();
    return subtotal + contingency;
  };

  return (
    <TabsContent value="investment" className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Site Acquisition</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Purchase Price</Label>
              <Input
                type="number"
                value={kpis.purchasePrice || kpis.budget?.siteAcquisition || 0}
                onChange={(e) => {
                  const value = Number(e.target.value) || 0;
                  handlePurchasePriceChange(value);
                }}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Real Estate Transfer Tax</Label>
              <Input
                type="number"
                value={kpis.budget?.transferTax || 0}
                onChange={(e) =>
                  handleBudgetChange("transferTax", Number(e.target.value) || 0)
                }
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Deal Costs</Label>
              <Input
                type="number"
                value={kpis.budget?.dealCosts || 0}
                onChange={(e) =>
                  handleBudgetChange("dealCosts", Number(e.target.value) || 0)
                }
              />
            </div>
          </div>
          <div className="p-3 bg-muted/30 rounded">
            <div className="flex justify-between">
              <span className="text-sm">Subtotal:</span>
              <span className="font-medium">
                {currency} {calculateSiteAcquisition().toLocaleString()}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Construction & FF&E</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Construction Costs</Label>
              <Input
                type="number"
                value={
                  kpis.budget?.construction ||
                  kpis.investmentBudget?.constructionCosts ||
                  0
                }
                onChange={(e) => {
                  const value = Number(e.target.value) || 0;
                  handleCombinedChange(
                    "construction",
                    "constructionCosts",
                    value
                  );
                }}
              />
              <p className="text-xs text-muted-foreground">
                Benchmark: 500-4,000 {currency}/sqm
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">FF&E and OS&E</Label>
              <Input
                type="number"
                value={
                  kpis.budget?.ffoeOse || kpis.investmentBudget?.ffeOse || 0
                }
                onChange={(e) => {
                  const value = Number(e.target.value) || 0;
                  handleCombinedChange("ffoeOse", "ffeOse", value);
                }}
              />
              <p className="text-xs text-muted-foreground">
                Benchmark: 5,000-40,000 {currency}/room
              </p>
            </div>
          </div>
          <div className="p-3 bg-muted/30 rounded">
            <div className="flex justify-between">
              <span className="text-sm">Subtotal:</span>
              <span className="font-medium">
                {currency} {calculateConstruction().toLocaleString()}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Development Costs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Professional Fees</Label>
              <Input
                type="number"
                value={
                  kpis.budget?.profFees ||
                  kpis.investmentBudget?.professionalFees ||
                  0
                }
                onChange={(e) => {
                  const value = Number(e.target.value) || 0;
                  handleCombinedChange("profFees", "professionalFees", value);
                }}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Planning & Municipality</Label>
              <Input
                type="number"
                value={kpis.budget?.planningFees || 0}
                onChange={(e) =>
                  handleBudgetChange(
                    "planningFees",
                    Number(e.target.value) || 0
                  )
                }
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Development Fee</Label>
              <Input
                type="number"
                value={
                  kpis.budget?.devFee ||
                  kpis.investmentBudget?.developmentFee ||
                  0
                }
                onChange={(e) => {
                  const value = Number(e.target.value) || 0;
                  handleCombinedChange("devFee", "developmentFee", value);
                }}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="space-y-2">
              <Label className="text-sm">Insurance & Admin</Label>
              <Input
                type="number"
                value={kpis.budget?.insuranceAdmin || 0}
                onChange={(e) =>
                  handleBudgetChange(
                    "insuranceAdmin",
                    Number(e.target.value) || 0
                  )
                }
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Other</Label>
              <Input
                type="number"
                value={
                  kpis.budget?.other || kpis.investmentBudget?.otherDev || 0
                }
                onChange={(e) => {
                  const value = Number(e.target.value) || 0;
                  handleCombinedChange("other", "otherDev", value);
                }}
              />
            </div>
          </div>
          <div className="p-3 bg-muted/30 rounded">
            <div className="flex justify-between">
              <span className="text-sm">Subtotal:</span>
              <span className="font-medium">
                {currency} {calculateDevelopment().toLocaleString()}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Other Costs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Pre-opening Budget</Label>
              <Input
                type="number"
                value={
                  kpis.budget?.preopening ||
                  kpis.investmentBudget?.preOpening ||
                  0
                }
                onChange={(e) => {
                  const value = Number(e.target.value) || 0;
                  handleCombinedChange("preopening", "preOpening", value);
                }}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Contingency (%)</Label>
              <Input
                type="number"
                value={
                  kpis.budget?.contingencyPct ||
                  kpis.investmentBudget?.contingencyPct ||
                  10
                }
                onChange={(e) => {
                  const value = Number(e.target.value) || 0;
                  handleCombinedChange(
                    "contingencyPct",
                    "contingencyPct",
                    value
                  );
                }}
              />
            </div>
          </div>
          <div className="p-3 bg-muted/30 rounded">
            <div className="flex justify-between">
              <span className="text-sm">Pre-opening Subtotal:</span>
              <span className="font-medium">
                {currency} {calculatePreOpening().toLocaleString()}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <div className="p-4 bg-muted/30 rounded-lg border">
          <h3 className="font-semibold mb-3">Investment Budget Summary</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span>Site Acquisition:</span>
              <span>
                {currency} {calculateSiteAcquisition().toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Construction & FF&E:</span>
              <span>
                {currency} {calculateConstruction().toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Development Costs:</span>
              <span>
                {currency} {calculateDevelopment().toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Pre-opening:</span>
              <span>
                {currency} {calculatePreOpening().toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span>Subtotal:</span>
              <span>
                {currency}{" "}
                {(
                  calculateSiteAcquisition() +
                  calculateConstruction() +
                  calculateDevelopment() +
                  calculatePreOpening()
                ).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Contingency ({kpis.budget?.contingencyPct || 10}%):</span>
              <span>
                {currency} {calculateContingency().toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between border-t pt-2 font-bold text-lg">
              <span>Grand Total (excl. VAT):</span>
              <span>
                {currency} {calculateGrandTotal().toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    </TabsContent>
  );
}
